import React from "react";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { WarningAmberOutlined } from "@mui/icons-material";
import { tokens } from "../../theme";

/**
 * One person, with the property links that decide what their Moksha access actually covers.
 *
 * The flag sits on the person, so granting it reaches every hotel they can log into. That
 * reach is the thing worth showing.
 *
 * Live and dormant links are separated under their own headings rather than mixed in one
 * chip row: the heading carries the meaning, so a dormant link does not have to explain
 * itself in a label and end up wider - and louder - than the live ones it sits beside.
 */
const NAME_LIMIT = 5;

export const nameList = (properties, limit) => {
    const names = properties.map((property) => property?.name).filter(Boolean);
    if (names.length <= limit) return names.join(", ");
    return `${names.slice(0, limit).join(", ")}, +${names.length - limit} more`;
};

const StaffCard = ({ staff, action }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const name = [staff?.first_name, staff?.last_name].filter(Boolean).join(" ") || "Unnamed user";
    const properties = staff?.properties ?? [];
    const live = properties.filter((p) => p?.link_active && p?.hotel_active);
    const dormant = properties.filter((p) => !(p?.link_active && p?.hotel_active));
    const liveCount = staff?.active_property_count ?? live.length;

    return (
        <Box
            sx={{
                backgroundColor: colors.primary[400],
                borderRadius: 1,
                borderLeft: `4px solid ${staff?.is_moksha_staff ? colors.greenAccent[500] : colors.grey[700]}`,
                p: 2.5,
                mb: 1.5,
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography fontWeight="bold" color={colors.grey[100]} fontSize={16}>
                            {name}
                        </Typography>
                        {staff?.is_moksha_staff && (
                            <Chip
                                size="small"
                                label="Moksha access"
                                sx={{
                                    backgroundColor: colors.greenAccent[800],
                                    color: colors.greenAccent[300],
                                    fontSize: 11,
                                }}
                            />
                        )}
                        {!staff?.is_active && (
                            <Chip
                                size="small"
                                label="Account disabled"
                                sx={{ backgroundColor: colors.grey[800], color: colors.grey[200], fontSize: 11 }}
                            />
                        )}
                    </Box>

                    <Typography color={colors.grey[300]} fontSize={13.5} mt={0.5}>
                        {staff?.email}
                    </Typography>
                </Box>

                <Box flexShrink={0}>{action}</Box>
            </Box>

            {/* What this person's access covers */}
            <Box mt={2} pt={2} sx={{ borderTop: `1px solid ${colors.primary[300]}` }}>
                {liveCount > 0 ? (
                    <>
                        <Typography variant="caption" color={colors.grey[300]} display="block">
                            Sees Moksha bookings at {liveCount} {liveCount === 1 ? "property" : "properties"}
                        </Typography>
                        <Typography color={colors.grey[100]} fontSize={13.5} mt={0.5}>
                            {nameList(live, NAME_LIMIT)}
                        </Typography>
                    </>
                ) : (
                    <Box display="flex" alignItems="flex-start" gap={0.75}>
                        <WarningAmberOutlined sx={{ fontSize: 16, color: colors.grey[400], mt: "2px" }} />
                        <Typography color={colors.grey[200]} fontSize={13}>
                            {staff?.is_moksha_staff
                                ? "Their access covers nothing right now — they work at no active property."
                                : "Granting access now has no effect until they are given access to an active property."}
                        </Typography>
                    </Box>
                )}

                {dormant.length > 0 && (
                    <Box mt={liveCount > 0 ? 1.5 : 1.25}>
                        <Typography variant="caption" color={colors.grey[300]} display="block">
                            Dormant — would widen this if re-enabled
                        </Typography>
                        <Typography color={colors.grey[300]} fontSize={13} mt={0.25}>
                            {nameList(dormant, 3)}
                        </Typography>
                    </Box>
                )}

                {staff?.granted_at && (
                    <Typography variant="caption" color={colors.grey[300]} display="block" mt={1.5}>
                        Access last changed{" "}
                        {new Date(staff.granted_at).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                        })}
                        {staff?.granted_by ? ` by ${staff.granted_by}` : ""}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default StaffCard;
